/**
 * The backup file format.
 *
 * A backup is the only thing standing between a shop and losing its invoices, so
 * the format is designed to outlive the version that wrote it. Three rules:
 *
 *  1. **It is versioned, and the version is checked before anything is written.**
 *     A file from a future DNR Vyapar must be refused with an explanation, not
 *     half-restored. A file from an older one is migrated forward.
 *
 *  2. **It is self-describing.** The format version, the data version it was
 *     taken from, when it was taken, and which app took it all travel inside the
 *     file. A backup you cannot identify is a backup you cannot trust.
 *
 *  3. **It is plain JSON, one key per table.** No compression, no binary blob.
 *     A file a human can open with a text editor is a file that can be rescued
 *     with a text editor, which is the situation a backup exists for.
 *
 * WHAT IS DELIBERATELY NOT IN HERE
 * -------------------------------
 * No file paths, and no clock-dependent values other than the timestamp. A
 * backup must restore on a different machine, and one that carried the original
 * install's settings would silently point the new install at a company that does
 * not exist there.
 */

export const BACKUP_FORMAT = 'dnr-vyapar-backup';
export const BACKUP_FORMAT_VERSION = 1;

/** Tables restored, in dependency order. */
export const BACKUP_TABLES = [
  'businesses',
  'stockLocations',
  'categories',
  'taxRates',
  'parties',
  'products',
  'priceLists',
  'priceListItems',
  'documents',
  'documentLines',
  'documentLinks',
  'payments',
  'paymentAllocations',
  'expenses',
  'stockTransactions',
  'documentSequences',
  'settings',
  'auditLogs',
] as const;

export type BackupTable = (typeof BACKUP_TABLES)[number];

export interface BackupFile {
  /** Identifies the file, so an arbitrary JSON cannot be mistaken for a backup. */
  readonly format: typeof BACKUP_FORMAT;
  readonly formatVersion: number;
  /** The app that wrote it. */
  readonly app: 'DNR_Vyapar_2';
  /** Schema/data version the data was at when the backup was taken. */
  readonly dataVersion: number;
  /** ISO timestamp. Informational; never used to decide anything. */
  readonly takenAt: string;
  /** Businesses included, so a scoped restore can be shown before it runs. */
  readonly businessIds: readonly string[];
  /** One key per table. */
  readonly tables: { readonly [K in BackupTable]: readonly unknown[] };
}

export type BackupProblem =
  | 'NOT_A_BACKUP'
  | 'FUTURE_FORMAT'
  | 'UNSUPPORTED_FORMAT'
  | 'CORRUPT';

export interface BackupValidation {
  readonly ok: boolean;
  readonly problem?: BackupProblem;
  readonly message?: string;
  /** Present when valid, for a restore preview. */
  readonly dataVersion?: number;
  readonly businessIds?: readonly string[];
  readonly rowCount?: number;
}

const OK: BackupValidation = { ok: true };

/**
 * Check a parsed backup BEFORE a single row is written.
 *
 * Refusing first is the entire point. A restore that writes half a database and
 * then discovers the file is from a newer version is worse than no restore,
 * because the user is left with a database that looks populated and is wrong.
 */
export function validateBackup(parsed: unknown): BackupValidation {
  if (typeof parsed !== 'object' || parsed === null) {
    return { ok: false, problem: 'NOT_A_BACKUP', message: 'That file is not a DNR Vyapar backup.' };
  }
  const file = parsed as Partial<BackupFile>;

  if (file.format !== BACKUP_FORMAT) {
    return { ok: false, problem: 'NOT_A_BACKUP', message: 'That file is not a DNR Vyapar backup.' };
  }

  const version = Number(file.formatVersion);
  if (!Number.isFinite(version) || version < 1) {
    return {
      ok: false,
      problem: 'UNSUPPORTED_FORMAT',
      message: 'The backup file does not say which format version it is.',
    };
  }
  if (version > BACKUP_FORMAT_VERSION) {
    // Refused, not guessed at. A newer DNR Vyapar may write tables this one has
    // never heard of, and restoring only the ones it recognises would produce a
    // database that is missing data while looking complete.
    return {
      ok: false,
      problem: 'FUTURE_FORMAT',
      message: `This backup was written by a newer version of DNR Vyapar (format ${version}). Update the app before restoring it.`,
    };
  }

  const tables = file.tables;
  if (typeof tables !== 'object' || tables === null) {
    return { ok: false, problem: 'CORRUPT', message: 'The backup file has no data in it.' };
  }

  let rowCount = 0;
  for (const name of BACKUP_TABLES) {
    const rows = (tables as Record<string, unknown>)[name];
    // A missing table is treated as EMPTY, not as an error: a backup taken
    // before a table existed is a legitimate file, and demanding every key
    // would refuse it for the sake of a completeness this format never promised.
    if (rows === undefined) continue;
    if (!Array.isArray(rows)) {
      return {
        ok: false,
        problem: 'CORRUPT',
        message: `The section "${name}" in the backup is not a list of rows.`,
      };
    }
    rowCount += rows.length;
  }

  if (rowCount === 0) {
    return { ok: false, problem: 'CORRUPT', message: 'The backup contains no records at all.' };
  }

  return {
    ...OK,
    dataVersion: Number(file.dataVersion ?? 0),
    businessIds: Array.isArray(file.businessIds) ? file.businessIds : [],
    rowCount,
  };
}

/** Parse a file's text and validate it, in one step. */
export function parseBackup(text: string): BackupValidation & { readonly file?: BackupFile } {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return {
      ok: false,
      problem: 'CORRUPT',
      message: 'The backup file is not readable as DNR Vyapar backup data.',
    };
  }
  const result = validateBackup(parsed);
  return result.ok ? { ...result, file: parsed as BackupFile } : result;
}

/** A filename that sorts and reads sensibly, with no id baked into it. */
export function backupFileName(takenAt: string, businessLabel: string): string {
  const stamp = takenAt.slice(0, 10);
  const safe = businessLabel.replace(/[^A-Za-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'all';
  return `dnr-vyapar-backup-${stamp}-${safe}.json`;
}

